import React, { useEffect, useMemo, useState } from "react";
import AuthPage from "./AuthPage";
import AninaTradeHomePage from "../pages/AninaTradeHomePage";
import { useAuth } from "./AuthContext";
import AdminPortal from "../components/AdminPortal";
import OwnerPortal from "../components/OwnerPortal";
import { UserStorageProvider } from "../storage/UserStorageContext";
import { createUserScopedStorage } from "../storage/userScopedStorage";

function passwordRecoveryRouteActive() {
  const params = new URLSearchParams(window.location.search);
  return params.get("auth") === "reset-password" || window.location.hash.includes("type=recovery");
}

function AuthGate({ children }) {
  const { user, loading, isAdmin, isOwner, logout } = useAuth();
  const [adminRoute, setAdminRoute] = useState(window.location.pathname === "/admin");
  const [ownerRoute, setOwnerRoute] = useState(window.location.pathname === "/owner");
  const [recoveryRoute, setRecoveryRoute] = useState(passwordRecoveryRouteActive());
  const [publicAuthOpen, setPublicAuthOpen] = useState(false);
  const [guestExploreOpen, setGuestExploreOpen] = useState(false);

  const userStorage = useMemo(
    () => (user?.id ? createUserScopedStorage(user.id) : null),
    [user?.id],
  );

  useEffect(() => {
    const syncRoute = () => {
      setAdminRoute(window.location.pathname === "/admin");
      setOwnerRoute(window.location.pathname === "/owner");
      setRecoveryRoute(passwordRecoveryRouteActive());
    };
    window.addEventListener("popstate", syncRoute);
    window.addEventListener("anina:auth-route", syncRoute);
    return () => {
      window.removeEventListener("popstate", syncRoute);
      window.removeEventListener("anina:auth-route", syncRoute);
    };
  }, []);

  if (loading) {
    return <div className="authLoading">Verifying secure AninaTrade session…</div>;
  }

  const handlePublicNavigate = (page) => {
    if (page === "sign-in" || page === "create-account") {
      const authMode =
        page === "create-account" ? "create-account" : "sign-in";

      window.history.replaceState(
        window.history.state || {},
        document.title,
        `/?auth=${authMode}`,
      );

      setGuestExploreOpen(false);
      setPublicAuthOpen(true);
      return;
    }

    window.history.replaceState(
      {
        ...(window.history.state || {}),
        aninaGuestPage: page,
      },
      document.title,
    );

    setPublicAuthOpen(false);
    setGuestExploreOpen(true);
  };

 if (recoveryRoute || (!user && publicAuthOpen)) {
  return <AuthPage />;
}

if (!user && guestExploreOpen) {
  return children;
}

if (!user || !userStorage) {
  return (
    <AninaTradeHomePage
      onNavigate={handlePublicNavigate}
    />
  );
}

  const navigatePrivileged = (path) => {
    window.history.pushState({}, document.title, path);
    window.dispatchEvent(new Event("anina:auth-route"));
  };

  const closePrivilegedPortal = () => navigatePrivileged("/");

  return (
    <UserStorageProvider storage={userStorage}>
      {children}
      <div className="authSessionBar" aria-label="Authenticated account controls">
        <span title={user.email}>{user.email}</span>
        {isAdmin && (
          <button type="button" className="ownerAccessButton" onClick={() => navigatePrivileged("/admin")}>
            Admin Portal
          </button>
        )}
        {isOwner && (
          <button type="button" className="ownerAccessButton" onClick={() => navigatePrivileged("/owner")}>
            Owner Portal
          </button>
        )}
        <button type="button" onClick={logout}>Sign out</button>
      </div>

      {adminRoute && isAdmin && <AdminPortal onClose={closePrivilegedPortal} />}
      {adminRoute && !isAdmin && (
        <div className="ownerPortalBackdrop">
          <section className="ownerPortal" role="alert">
            <header>
              <div>
                <span>ACCESS BLOCKED</span>
                <h2>Admin access required</h2>
                <p>Your authenticated account does not have the server-managed admin or owner role.</p>
              </div>
              <button type="button" onClick={closePrivilegedPortal} aria-label="Close">×</button>
            </header>
          </section>
        </div>
      )}

      {ownerRoute && isOwner && <OwnerPortal onClose={closePrivilegedPortal} />}
      {ownerRoute && !isOwner && (
        <div className="ownerPortalBackdrop">
          <section className="ownerPortal" role="alert">
            <header>
              <div>
                <span>ACCESS BLOCKED</span>
                <h2>Owner access required</h2>
                <p>Your authenticated account does not have the server-managed owner role.</p>
              </div>
              <button type="button" onClick={closePrivilegedPortal} aria-label="Close">×</button>
            </header>
          </section>
        </div>
      )}
    </UserStorageProvider>
  );
}

export default AuthGate;
